import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Pencil, X } from 'lucide-react';
import ExportButton from './ExportButton';

const PROJECTS_URL = `${API_URL}/projects`;

const PortfolioPage = ({ showToast }) => {

    const [projects, setProjects] = useState([]);

    const [showAddProject, setShowAddProject] = useState(false);

    const [editingProject, setEditingProject] = useState(null);

    const [showEditProject, setShowEditProject] = useState(false);

    const [newProject, setNewProject] = useState({
        title: '',
        subtitle: '',
        description: '',
        tags: '',
        button_text: '',
        image: '',
        link: ''
    });

    // ================= FETCH PROJECTS =================


    const fetchProjects = useCallback(async () => {

        try {

            const token = localStorage.getItem('admin_token');

            const response = await axios.get(PROJECTS_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            console.log("FULL API RESPONSE:", response.data);

            let projectsData = [];

            // CASE 1
            if (Array.isArray(response.data)) {
                projectsData = response.data;
            }

            // CASE 2
            else if (Array.isArray(response.data.projects)) {
                projectsData = response.data.projects;
            }

            // CASE 3
            else if (Array.isArray(response.data.data)) {
                projectsData = response.data.data;
            }

            // CASE 4
            else if (
                response.data.success &&
                Array.isArray(response.data.data)
            ) {
                projectsData = response.data.data;
            }

            // CLEAN INVALID DATA
            projectsData = projectsData.filter(
                (p) => p && typeof p === 'object'
            );

            setProjects(projectsData);

        } catch (error) {

            console.error("FETCH ERROR:", error);

            if (error.response) {
                console.log("ERROR RESPONSE:", error.response.data);
            }

            if (error.response?.status === 401) {

                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');

                window.location.href = '/admin/login';

                return;
            }

            setProjects([]);

            showToast?.('Failed to load projects', 'error');
        }
    }, [showToast]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    // ================= ADD PROJECT =================
    const handleAddProject = async (e) => {

        e.preventDefault();

        try {

            const token = localStorage.getItem('admin_token');

            const response = await axios.post(
                PROJECTS_URL,
                newProject,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("ADD RESPONSE:", response.data);

            const addedProject =
                response.data.project ||
                response.data.data ||
                response.data;

            if (
                addedProject &&
                typeof addedProject === 'object'
            ) {

                setProjects((prev) => [
                    addedProject,
                    ...prev
                ]);
            }

            // RESET FORM
            setNewProject({
                title: '',
                subtitle: '',
                description: '',
                tags: '',
                button_text: '',
                image: '',
                link: ''
            });

            setShowAddProject(false);

            showToast?.('Project added successfully', 'success');

        } catch (error) {

            console.error("ADD ERROR:", error);

            if (error.response) {
                console.log("ERROR RESPONSE:", error.response.data);
            }

            if (error.response?.status === 401) {

                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');

                window.location.href = '/admin/login';

                return;
            }

            showToast?.('Failed to add project', 'error');
        }
    };

    // ================= OPEN EDIT MODAL =================
    const handleEditProject = (project) => {

        setEditingProject({
            ...project
        });

        setShowEditProject(true);
    };

    // ================= UPDATE PROJECT =================
    const handleUpdateProject = async (e) => {

        e.preventDefault();

        try {

            const token = localStorage.getItem('admin_token');

            const response = await axios.put(
                `${PROJECTS_URL}/${editingProject.id}`,
                editingProject,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log("UPDATE RESPONSE:", response.data);

            const updatedProject =
                response.data.project ||
                response.data.data ||
                response.data;

            setProjects((prev) =>
                prev.map((p) =>
                    p.id === updatedProject.id
                        ? updatedProject
                        : p
                )
            );

            setShowEditProject(false);

            setEditingProject(null);

            showToast?.('Project updated successfully', 'success');

        } catch (error) {

            console.error("UPDATE ERROR:", error);

            if (error.response) {
                console.log("ERROR RESPONSE:", error.response.data);
            }

            showToast?.('Failed to update project', 'error');
        }
    };

    // ================= DELETE PROJECT =================
    const handleDeleteProject = async (id) => {

        if (!window.confirm('Delete this project?')) return;

        try {

            const token = localStorage.getItem('admin_token');

            await axios.delete(`${PROJECTS_URL}/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            setProjects((prev) =>
                prev.filter((p) => p.id !== id)
            );

            showToast?.('Project deleted successfully', 'success');

        } catch (error) {

            console.error("DELETE ERROR:", error);

            if (error.response) {
                console.log("ERROR RESPONSE:", error.response.data);
            }

            if (error.response?.status === 401) {

                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');

                window.location.href = '/admin/login';

                return;
            }

            showToast?.('Failed to delete project', 'error');
        }
    };

    return (

        <div className="space-y-6">

            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">

                <h2 className="text-xl font-bold text-dark-text">
                    Portfolio Projects
                </h2>

                <div className="flex gap-2">

                    <ExportButton
                        data={projects}
                        filename="projects"
                        label="Export"
                    />

                    <button
                        onClick={() => setShowAddProject(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-brand-teal text-white rounded-xl font-bold hover:bg-brand-light transition-all"
                    >
                        <Plus className="w-5 h-5" />

                        <span>Add Project</span>
                    </button>

                </div>

            </div>

            {/* PROJECT GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                <AnimatePresence>

                    {Array.isArray(projects) && projects.length > 0 ? (

                        projects
                            .filter((p) => p && typeof p === 'object')
                            .map((p) => (

                                <motion.div
                                    key={p.id || Math.random()}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm group"
                                >

                                    {/* IMAGE */}
                                    <div className="h-48 relative overflow-hidden bg-slate-100">

                                        <img
                                            src={
                                                p?.image ||
                                                'https://via.placeholder.com/600x400?text=No+Image'
                                            }
                                            alt={p?.title || 'Project'}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />

                                        {/* ACTION BUTTONS */}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">

                                            {/* EDIT */}
                                            <button
                                                onClick={() => handleEditProject(p)}
                                                className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all"
                                            >
                                                <Pencil className="w-5 h-5" />
                                            </button>

                                            {/* DELETE */}
                                            <button
                                                onClick={() => handleDeleteProject(p.id)}
                                                className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>

                                        </div>

                                    </div>

                                    {/* CONTENT */}
                                    <div className="p-6">

                                        <span className="text-[10px] uppercase tracking-widest text-brand-teal font-bold">
                                            {p.tags || 'PROJECT'}
                                        </span>

                                        <h3 className="text-lg font-bold text-dark-text mt-1">
                                            {p.title || 'Untitled Project'}
                                        </h3>

                                        {p.subtitle && (
                                            <p className="text-sm text-gray-500 mt-1">
                                                {p.subtitle}
                                            </p>
                                        )}

                                        {p.description && (
                                            <p className="text-sm text-gray-600 mt-3">
                                                {p.description}
                                            </p>
                                        )}

                                        {p.link && (
                                            <a
                                                href={p.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-blue-500 hover:underline mt-4 inline-block"
                                            >
                                                {p.button_text || 'Visit Project'}
                                            </a>
                                        )}

                                    </div>

                                </motion.div>

                            ))

                    ) : (

                        <div className="col-span-full p-8 text-center text-gray-500 border border-dashed border-slate-300 rounded-2xl">
                            No projects found.
                        </div>

                    )}

                </AnimatePresence>

            </div>

            {/* ADD PROJECT MODAL */}
            {showAddProject && (

                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6">

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white p-8 rounded-[32px] w-full max-w-lg relative"
                    >

                        <button
                            onClick={() => setShowAddProject(false)}
                            className="absolute top-6 right-6 text-gray-400 hover:text-black"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold mb-8">
                            Add New Project
                        </h2>

                        <form
                            onSubmit={handleAddProject}
                            className="space-y-4"
                        >

                            <input
                                required
                                type="text"
                                placeholder="Project Title"
                                value={newProject.title}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        title: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Subtitle"
                                value={newProject.subtitle}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        subtitle: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <textarea
                                placeholder="Description"
                                value={newProject.description}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        description: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Tags"
                                value={newProject.tags}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        tags: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Button Text"
                                value={newProject.button_text}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        button_text: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                required
                                type="text"
                                placeholder="Image URL"
                                value={newProject.image}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        image: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Project Link"
                                value={newProject.link}
                                onChange={(e) =>
                                    setNewProject({
                                        ...newProject,
                                        link: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <button
                                type="submit"
                                className="w-full py-4 bg-brand-teal text-white font-bold rounded-xl hover:bg-brand-light transition-all"
                            >
                                Publish Project
                            </button>

                        </form>

                    </motion.div>

                </div>

            )}

            {/* EDIT PROJECT MODAL */}
            {showEditProject && editingProject && (

                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6">

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white p-8 rounded-[32px] w-full max-w-lg relative"
                    >

                        <button
                            onClick={() => setShowEditProject(false)}
                            className="absolute top-6 right-6 text-gray-400 hover:text-black"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold mb-8">
                            Edit Project
                        </h2>

                        <form
                            onSubmit={handleUpdateProject}
                            className="space-y-4"
                        >

                            <input
                                type="text"
                                placeholder="Project Title"
                                value={editingProject.title || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        title: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Subtitle"
                                value={editingProject.subtitle || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        subtitle: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <textarea
                                placeholder="Description"
                                value={editingProject.description || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        description: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Tags"
                                value={editingProject.tags || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        tags: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Button Text"
                                value={editingProject.button_text || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        button_text: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Image URL"
                                value={editingProject.image || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        image: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <input
                                type="text"
                                placeholder="Project Link"
                                value={editingProject.link || ''}
                                onChange={(e) =>
                                    setEditingProject({
                                        ...editingProject,
                                        link: e.target.value
                                    })
                                }
                                className="w-full border border-slate-200 rounded-xl px-4 py-3"
                            />

                            <button
                                type="submit"
                                className="w-full py-4 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-600 transition-all"
                            >
                                Update Project
                            </button>

                        </form>

                    </motion.div>

                </div>

            )}

        </div>

    );
};

export default PortfolioPage;